import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  FormControl,
  TextField,
  MenuItem,
  Popper,
  Paper,
  ClickAwayListener,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_TIME_ZONES } from "../graphql/queries";

const TimezoneSelector = ({ timezoneId, onTimezoneSelect, isDisabled = false }) => {
  const [timezonesList, setTimezonesList] = useState([]);
  const [groupedTimezones, setGroupedTimezones] = useState({});
  const [currentTimes, setCurrentTimes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const didInitialNotifyRef = useRef(false);
  const inputRef = useRef(null);
  const { loading, error, data } = useQuery(GET_TIME_ZONES);

  const groupByRegion = (timezones) =>
    timezones.reduce((acc, tz) => {
      const regionName = tz.region?.name || "Other";
      if (!acc[regionName]) acc[regionName] = [];
      acc[regionName].push(tz);
      return acc;
    }, {});

  useEffect(() => {
    if (data?.getTimeZones) {
      setTimezonesList(data.getTimeZones);
      setGroupedTimezones(groupByRegion(data.getTimeZones));

      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const initialTimezone = timezoneId || browserTimezone;
      setSelectedTimezone(initialTimezone);

      if (!didInitialNotifyRef.current) {
        const initialTimezoneInfo = data.getTimeZones.find(
          (tz) =>
            Number(tz.id) === Number(initialTimezone) ||
            tz.timezone === initialTimezone?.timezone ||
            tz.timezone === initialTimezone
        );
        if (onTimezoneSelect && initialTimezoneInfo) {
          onTimezoneSelect(initialTimezoneInfo);
          didInitialNotifyRef.current = true;
        }
      }
    }
  }, [data, timezoneId, onTimezoneSelect]);

  useEffect(() => {
    const updateTimes = () => {
      const newTimes = {};
      timezonesList.forEach((tz) => {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: tz.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        newTimes[tz.timezone] = formatter.format(new Date());
      });
      setCurrentTimes(newTimes);
    };

    updateTimes();
    const interval = setInterval(updateTimes, 60000);
    return () => clearInterval(interval);
  }, [timezonesList]);

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = timezonesList.filter(
      (tz) =>
        tz.name.toLowerCase().includes(query) ||
        tz.timezone.toLowerCase().includes(query) ||
        tz.region?.name.toLowerCase().includes(query)
    );
    setGroupedTimezones(groupByRegion(filtered));
  };

  const handleClick = (event) => setAnchorEl(anchorEl ? null : event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectTimezone = (timezone) => {
    setSelectedTimezone(timezone);
    const selected = timezonesList.find((tz) => tz.timezone === timezone);
    if (onTimezoneSelect && selected) onTimezoneSelect(selected);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "timezone-popper" : undefined;

  const selectedTimezoneName = timezonesList.find(
    (tz) =>
      Number(tz.id) === Number(selectedTimezone) ||
      tz.timezone === selectedTimezone ||
      tz.timezone === selectedTimezone?.timezone
  )?.name;

  return (
    <Box sx={{ width: "100%" }}>
      {loading ? (
        <Typography>Loading Timezones...</Typography>
      ) : error ? (
        <Typography>Error loading timezones: {error.message}</Typography>
      ) : (
        <FormControl fullWidth>
          <OutlinedInput
            disabled={isDisabled}
            inputRef={inputRef}
            value={selectedTimezoneName || "Select timezone"}
            onClick={handleClick}
            readOnly
            startAdornment={
              <InputAdornment position="start">
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Timezone
                </Typography>
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <Typography sx={{ color: "text.secondary", fontSize: "0.72rem" }}>
                  ▼
                </Typography>
              </InputAdornment>
            }
            sx={{
              cursor: "pointer",
              borderRadius: 2,
              backgroundColor: "rgba(248,250,255,0.98)",
              "& .MuiOutlinedInput-input": {
                fontSize: "0.84rem",
                fontWeight: 500,
                padding: "10px 0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(29,78,216,0.16)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(29,78,216,0.3)",
              },
            }}
          />

          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            modifiers={[
              { name: "offset", options: { offset: [0, 6] } },
              { name: "flip", options: { fallbackPlacements: ["top-end", "bottom-start"] } },
              { name: "preventOverflow", options: { padding: 12 } },
            ]}
            sx={{
              zIndex: 1600,
              width: { xs: "calc(100vw - 24px)", sm: 420 },
              maxWidth: "calc(100vw - 24px)",
            }}
          >
            <Paper
              sx={{
                maxHeight: 360,
                overflowY: "auto",
                borderRadius: 2,
                boxShadow: "0 18px 40px rgba(15, 31, 46, 0.16)",
                border: "1px solid rgba(29,78,216,0.10)",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Box
                    sx={{
                      p: 1.25,
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                    }}
                  >
                    <TextField
                      placeholder="Search timezones"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </Box>
                  {Object.keys(groupedTimezones).map((region) => (
                    <Box key={region}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontWeight: 700,
                          px: 1.5,
                          py: 0.5,
                          color: "text.secondary",
                          backgroundColor: "rgba(29,78,216,0.04)",
                        }}
                      >
                        {region}
                      </Typography>
                      {groupedTimezones[region].map((tz) => (
                        <MenuItem
                          key={tz.id}
                          onClick={() => handleSelectTimezone(tz.timezone)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            whiteSpace: "normal",
                            py: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.3 }}>
                              {tz.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tz.timezone}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ flexShrink: 0 }}
                          >
                            {currentTimes[tz.timezone] || "Loading..."}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Box>
                  ))}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Popper>
        </FormControl>
      )}
    </Box>
  );
};

export default TimezoneSelector;
